"use client";

import type { FC } from "react";
import { useState } from "react";
import { PhoneIcon, UserIcon } from "@heroicons/react/24/outline";
import type { TaxonomyOption } from "cmw-client";
import { Button } from "@/components/button";
import { MultiSelect, NestedMultiCombobox, toTreeOptions, TreeMultiCombobox } from "@/components/input";
import { Switcher } from "@/components/switcher";
import { activeFilters, filterCount, NO_FILTERS, type DirectoryFilters, type FilterKey } from "./filters";
import { GENDER_OPTIONS, PRACTITIONER_TYPE_OPTIONS, toFilterGroups, toFilterOptions } from "./options";

type AllFiltersPanelProps = {
  filters: DirectoryFilters;
  taxonomy?: Record<string, TaxonomyOption[]>;
  onApply: (filters: DirectoryFilters) => void;
  heading?: boolean;
};

const OTHERS: [FilterKey, string][] = [
  ["therapeuticApproaches", "Therapeutic approaches"],
  ["culturalBackground", "Cultural background"],
  ["languages", "Preferred language"],
  ["fundingPathways", "Funding pathways and fee arrangements"],
  ["thirdPartySchemes", "Third-party referrals and schemes"],
  ["ageGroups", "Age groups"],
];

/** Matches the bar's own fields: a field that grows a row per tick pushes the panel off-screen. */
const MAX_VISIBLE_TAGS = 2;

const SESSION_OPTIONS = [
  { value: "any", label: "Any" },
  { value: "in-person", label: "In-Person", icon: <UserIcon className="size-18" /> },
  { value: "telehealth", label: "Telehealth", icon: <PhoneIcon className="size-18" /> },
];

export const AllFiltersPanel: FC<AllFiltersPanelProps> = ({ filters, taxonomy, onApply, heading }) => {
  const [draft, setDraft] = useState<DirectoryFilters>(filters);

  const set = (key: FilterKey) => (values: string[]) =>
    setDraft((current) => ({ ...current, [key]: values }));

  const sessionFormat = draft.sessionFormat?.[0] ?? "any";
  const count = filterCount(draft);

  return (
    <div className="flex h-full min-h-0 flex-col">
      {heading && (
        <p className="-mx-24 -mt-24 mb-24 rounded-t-large bg-background-grey px-24 py-16 text-21 leading-[1.2] text-neutral-black">
          All Filters
        </p>
      )}

      <div className="flex min-h-0 flex-1 flex-col gap-24 overflow-y-auto pb-24">
        {!heading && (
          <>
            <MultiSelect
              label="Gender preference"
              placeholder="Please select"
              maxVisibleTags={MAX_VISIBLE_TAGS}
              options={GENDER_OPTIONS}
              value={draft.gender ?? []}
              onValueChange={set("gender")}
            />

            <NestedMultiCombobox
              label="Areas of support"
              placeholder="Showing all"
              maxVisibleTags={MAX_VISIBLE_TAGS}
              groups={toFilterGroups(taxonomy?.focusAreas)}
              value={draft.focusAreas ?? []}
              onValueChange={set("focusAreas")}
            />

            <MultiSelect
              label="Client type"
              placeholder="Please select"
              maxVisibleTags={MAX_VISIBLE_TAGS}
              options={toFilterOptions(taxonomy?.clientTypes)}
              value={draft.clientTypes ?? []}
              onValueChange={set("clientTypes")}
            />
          </>
        )}

        <MultiSelect
          label="Profession type"
          placeholder="Please select"
          maxVisibleTags={MAX_VISIBLE_TAGS}
          options={PRACTITIONER_TYPE_OPTIONS}
          value={draft.practitionerType ?? []}
          onValueChange={set("practitionerType")}
        />

        <div className="flex flex-col gap-8">
          <span className="text-16 text-neutral-black">Session Types</span>
          <Switcher
            className="w-full"
            options={SESSION_OPTIONS}
            value={sessionFormat}
            onValueChange={(value: string) =>
              setDraft((current) => ({ ...current, sessionFormat: value === "any" ? [] : [value] }))
            }
          />
        </div>

        <hr className="border-neutral-light-grey" />

        <p className="text-18 font-medium text-neutral-black/70">Others</p>

        {OTHERS.map(([key, text]) =>
          key === "culturalBackground" ? (
            /* Everyone holding a country holds its continent too, so a continent sent alongside one of
               its countries would find the whole continent: the search gets the country alone. */
            <TreeMultiCombobox
              key={key}
              label={text}
              placeholder="Please select"
              maxVisibleTags={MAX_VISIBLE_TAGS}
              options={toTreeOptions(taxonomy?.[key])}
              keepImpliedParents={false}
              value={draft[key] ?? []}
              onValueChange={set(key)}
            />
          ) : (
            <MultiSelect
              key={key}
              label={text}
              placeholder="Please select"
              maxVisibleTags={MAX_VISIBLE_TAGS}
              options={toFilterOptions(taxonomy?.[key])}
              value={draft[key] ?? []}
              onValueChange={set(key)}
            />
          ),
        )}
      </div>

      <div className="flex shrink-0 gap-16 border-t border-neutral-light-grey pt-24">
        {/* Clearing applies straight away: emptying only the draft looks like nothing happened
            unless the reader then thinks to press Apply. */}
        <Button
          variant="secondary-white"
          size="m"
          className="flex-1 border-2"
          onClick={() => {
            setDraft(NO_FILTERS);
            onApply(NO_FILTERS);
          }}
        >
          Clear All Filters{count > 0 ? ` (${count})` : ""}
        </Button>
        <Button size="m" className="flex-1" onClick={() => onApply(activeFilters(draft))}>
          Apply Filters
        </Button>
      </div>
    </div>
  );
};
